/** Das Regal: Bücher stehen von vorn auf echten Holzbrettern.
 *
 *  Die Zeilenaufteilung passiert in JavaScript, weil ein Brett erst gezeichnet
 *  werden kann, wenn feststeht, welche Bände darauf stehen. Die Drehung der
 *  Bände folgt einer einzigen Kamera in der Regalmitte — keine Zufallswinkel.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Book } from "../../../shared/types";
import { coverUrl } from "../lib/api";

/** Breite und Dicke aus der Seitenzahl — dickere Bücher stehen breiter da. */
function masse(book: Book, einheit: number) {
  const seed = Number((book.isbn || String(book.id)).slice(-4)) || 1;
  const seiten = book.pages || 210 + (seed % 430);
  return {
    w: Math.round(einheit * (0.9 + (Math.min(seiten, 880) / 880) * 0.18)),
    d: Math.max(7, Math.round(6 + (Math.min(seiten, 900) / 900) * 17)),
  };
}

function Buch({
  book,
  einheit,
  yaw,
  onOeffnen,
}: {
  book: Book;
  einheit: number;
  yaw: number;
  onOeffnen: (b: Book) => void;
}) {
  const { w, d } = masse(book, einheit);
  const bild = book.cover ? coverUrl(book.cover_key ?? book.isbn, "s") : null;
  const ruecken = book.cover
    ? coverUrl(book.cover_key ?? book.isbn, "spine")
    : null;
  const autor = (book.author || "").split(",")[0];

  const marke = book.fav
    ? "★"
    : book.also
      ? "◆"
      : book.status === "dnf"
        ? "◦"
        : null;
  const markeTitel = book.fav
    ? "Lieblingsbuch"
    : book.also
      ? "steht auch im anderen Regal"
      : book.status === "dnf"
        ? "abgebrochen"
        : "";

  return (
    <button
      className="bk"
      style={
        {
          "--w": `${w}px`,
          "--d": `${d}px`,
          "--yaw": `${yaw.toFixed(2)}deg`,
          ...(ruecken ? { "--spineimg": `url(${ruecken})` } : {}),
        } as React.CSSProperties
      }
      onClick={() => onOeffnen(book)}
      aria-label={`${book.title} von ${book.author}`}
    >
      <span className="stapel">
        <span className="ruecken" />
        <span className="kopfschnitt" />
        <span className="schnitt" />
        <span className="deckel">
          {bild ? (
            <img src={bild} alt="" loading="lazy" decoding="async" />
          ) : (
            <span className="ohnebild">
              <b>{book.title}</b>
              <i>{autor}</i>
            </span>
          )}
          <span className="grade" />
          <span className="kante" />
        </span>
      </span>
      <span className="aufstand" />
      {marke && (
        <span className="marke" title={markeTitel}>
          {marke}
        </span>
      )}
      <span className="schild">
        <b>{book.title}</b>
        <i>{autor}</i>
      </span>
    </button>
  );
}

export interface BrettDaten {
  titel: string;
  bemerkung?: string;
  books: Book[];
}

export function Regal({
  bretter,
  onOeffnen,
}: {
  bretter: BrettDaten[];
  onOeffnen: (b: Book) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [breite, setBreite] = useState(1200);

  useLayoutEffect(() => {
    const messen = () => setBreite(ref.current?.clientWidth || 1200);
    messen();
    const ro = new ResizeObserver(messen);
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  // Kleinere Bände, mehr pro Reihe — riesige Cover wirken plakativ
  const einheit = Math.max(
    84,
    Math.min(146, breite / Math.max(4, Math.round(breite / 158))),
  );
  const luecke = Math.max(14, Math.min(34, breite * 0.019));

  return (
    <div ref={ref}>
      {bretter.map((brett) => {
        if (!brett.books.length) return null;
        // Zeilen füllen, bis das Brett voll ist
        const zeilen: Book[][] = [];
        let aktuell: Book[] = [];
        let belegt = 0;
        for (const b of brett.books) {
          const { w } = masse(b, einheit);
          if (aktuell.length && belegt + w + luecke > breite) {
            zeilen.push(aktuell);
            aktuell = [];
            belegt = 0;
          }
          aktuell.push(b);
          belegt += w + luecke;
        }
        if (aktuell.length) zeilen.push(aktuell);

        return (
          <section key={brett.titel}>
            <h3 className="brettname">
              {brett.titel} <em>{brett.books.length}</em>
              {brett.bemerkung && (
                <em style={{ letterSpacing: "0.06em" }}>{brett.bemerkung}</em>
              )}
            </h3>
            {zeilen.map((zeile, zi) => (
              <div key={zi}>
                <div className="reihe">
                  {zeile.map((b, i) => {
                    // Eine Kamera in der Regalmitte: Bände links zeigen ihre rechte
                    // Seite, Bände rechts ihre linke. Vorzeichen am Bildschirm
                    // gemessen, nicht hergeleitet.
                    const mitte = (zeile.length - 1) / 2;
                    const abstand =
                      zeile.length > 1 ? (mitte - i) / Math.max(mitte, 0.5) : 0;
                    return (
                      <Buch
                        key={b.id}
                        book={b}
                        einheit={einheit}
                        yaw={abstand * 17}
                        onOeffnen={onOeffnen}
                      />
                    );
                  })}
                  <span className="leiste" />
                </div>
                <div className="brett" />
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}

/** Der Saal im Hintergrund. Drei Fotografien, Auswahl bleibt gespeichert. */
const RAEUME = [
  { datei: "Clementinum_baroque_library_4.jpg", name: "Klementinum" },
  { datei: "Austria_-_Melk_Abbey_Library.jpg", name: "Melk" },
  { datei: "County_Dublin_-_Long_Room.jpg", name: "Trinity" },
];

export function Saal() {
  const [raum, setRaum] = useState(() => {
    try {
      return localStorage.getItem("buecherregal.saal") || RAEUME[0].datei;
    } catch {
      return RAEUME[0].datei;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("buecherregal.saal", raum);
    } catch {
      /* egal */
    }
  }, [raum]);

  return (
    <>
      <div className="raum">
        <img src={`/fotos/${raum}`} alt="" />
      </div>
      <div className="schleier" />
      <div
        style={{
          position: "fixed",
          bottom: 14,
          left: 16,
          zIndex: 20,
          display: "flex",
          gap: 14,
          alignItems: "baseline",
        }}
      >
        <span className="klein" style={{ fontSize: 9, opacity: 0.55 }}>
          Saal
        </span>
        {RAEUME.map((r) => (
          <button
            key={r.datei}
            className="klein"
            style={{
              fontSize: 10,
              color:
                raum === r.datei ? "var(--paper)" : "rgba(244,237,224,0.34)",
              borderBottom:
                raum === r.datei
                  ? "1px solid var(--brass)"
                  : "1px solid transparent",
              paddingBottom: 3,
            }}
            aria-pressed={raum === r.datei}
            onClick={() => setRaum(r.datei)}
          >
            {r.name}
          </button>
        ))}
      </div>
    </>
  );
}
